import './styles/global.css';
import { mount } from 'svelte';
import App from './components/AppWithNavigation.svelte';
import TelegramRedirect from './components/TelegramRedirect.svelte';
import { registerSW } from 'virtual:pwa-register';
import { environment } from './stores/environment';
import { telegram } from './stores/telegram';
import { themeStore } from './stores/theme.svelte';

// Initialize theme immediately (before mounting)
if (typeof window !== 'undefined') {
  // Force theme store initialization
  themeStore.theme;
}

// Redirect tg.legere.app to Telegram mini app (only if NOT already in Telegram)
if (typeof window !== 'undefined' && window.location.hostname === 'tg.legere.app') {
  // Check if we're already in Telegram Mini App context
  // If we have Telegram params or referrer, we're already inside Telegram - don't redirect
  const urlParams = new URLSearchParams(window.location.search);
  const hasTelegramParams = urlParams.has('tgWebAppData') || 
                           urlParams.has('tgWebAppVersion') || 
                           urlParams.has('tgWebAppPlatform');
  const fromTelegram = document.referrer && document.referrer.includes('t.me');
  const hash = window.location.hash;
  const hasTelegramHash = hash && (hash.includes('tgWebAppData=') || hash.includes('tgWebAppVersion='));
  const isTelegramUA = navigator.userAgent.toLowerCase().includes('telegram');
  
  const isInTelegram = hasTelegramParams || fromTelegram || hasTelegramHash || isTelegramUA;
  
  // Only redirect if we're NOT in Telegram (e.g., opened in regular browser)
  if (!isInTelegram) {
    window.location.href = 'https://t.me/legere_library_bot/app';
    // Stop execution after redirect
    throw new Error('Redirecting to Telegram mini app');
  }
  
  // If we're already in Telegram, continue normal execution
  console.log('Already in Telegram context, continuing normal execution');
}

let app: ReturnType<typeof mount>;

// Initialize environment detection and preload sample books
if (typeof window !== 'undefined') {
  // Detect environment (async to load Telegram SDK if needed)
  environment.initialize().catch((err) => {
    console.error('Failed to initialize environment:', err);
  });

  // Preload sample books on first launch
  import('./lib/preload-books').then(({ preloadSampleBooksIfNeeded }) => {
    preloadSampleBooksIfNeeded().catch((err) => {
      console.error('Failed to preload sample books:', err);
    });
  });
  
  // Register service worker with auto-update
  const updateSW = registerSW({
    onNeedRefresh() {
      if (confirm('New content available. Reload?')) {
        updateSW(true);
      }
    },
    onOfflineReady() {
      console.log('App ready to work offline');
    },
  });

  // Initialize environment detection
  if (typeof window !== 'undefined') {
    // Detect environment (async to load Telegram SDK if needed)
    environment.initialize().catch((err) => {
      console.error('Failed to initialize environment:', err);
    });
    
    // Subscribe to environment changes and initialize platform-specific features
    environment.subscribe(($env) => {
      if (!$env) return;

      console.log('Environment detected:', {
        type: $env.type,
        displayName: $env.displayName,
        isTelegram: $env.isTelegram,
        isCapacitor: $env.isCapacitor,
        isMobile: $env.isMobile,
      });

      // Initialize Telegram Mini App SDK if in Telegram environment
      if ($env.isTelegram) {
        console.log('Initializing Telegram Mini App SDK...');
        telegram.initialize();
        
        // Subscribe to Telegram data
        telegram.subscribe(($tg) => {
          if ($tg.isAvailable && $tg.user) {
            console.log('Telegram user:', $tg.user);
            console.log('Telegram platform:', $tg.webAppData?.platform);
            console.log('Telegram version:', $tg.webAppData?.version);
            console.log('Telegram color scheme:', $tg.webAppData?.colorScheme);
          }
        });
      }

      // Initialize Capacitor if on native platform
      if ($env.isCapacitor) {
        import('@capacitor/core').then(({ Capacitor }) => {
          if (Capacitor.isNativePlatform()) {
            console.log('Running on native platform:', Capacitor.getPlatform());

            // Initialize native plugins if needed
            import('@capacitor/status-bar').then(({ StatusBar, Style }) => {
              StatusBar.setStyle({ style: Style.Dark });
            }).catch(() => {
              // Status bar plugin not available
            });

            import('@capacitor/splash-screen').then(({ SplashScreen }) => {
              SplashScreen.hide();
            }).catch(() => {
              // Splash screen plugin not available
            });
          }
        });
      }
    });
  }

  // Mount Svelte 5 app
  app = mount(App, {
    target: document.getElementById('app')!,
  });
}

export default app;
